import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Plus, Trash2, MoreHorizontal, Upload, Paperclip, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { CommanServices } from '@/services/commanServices';
import AuthenticatedMedia from './AuthenticatedMedia';

// Severity levels with colors
const SEVERITY_LEVELS = {
  HIGH: { label: 'High', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800', borderColor: 'border-red-300' },
  MEDIUM: { label: 'Medium', color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-800', borderColor: 'border-orange-300' },
  LOW: { label: 'Low', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', borderColor: 'border-yellow-300' }
};

const EventTriggerManager = ({ 
  value = [], 
  onChange, 
  incidentSeverity = 'MEDIUM', // Incident-level severity for inheritance
  className = "",
  disabled = false 
}) => {
  const [newEventName, setNewEventName] = useState('');
  const [newTriggerInputs, setNewTriggerInputs] = useState({}); // Track input state for each event
  const [openPopovers, setOpenPopovers] = useState({}); // Track which popovers are open
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isUploadingDetailMedia, setIsUploadingDetailMedia] = useState(false);
  const [detailsModalConfig, setDetailsModalConfig] = useState({
    type: 'event', // 'event' or 'trigger'
    eventIndex: null,
    triggerIndex: null,
    triggerName: '',
    description: '',
    media: []
  });

  const openEventDetailsModal = (eventIndex) => {
    const et = value[eventIndex];
    setDetailsModalConfig({
      type: 'event',
      eventIndex: eventIndex,
      triggerIndex: null,
      triggerName: '',
      description: et.description || '',
      media: et.media || []
    });
    setIsDetailsModalOpen(true);
    setOpenPopovers({});
  };

  const openTriggerDetailsModal = (eventIndex, triggerIndex, triggerName) => {
    const et = value[eventIndex];
    setDetailsModalConfig({
      type: 'trigger',
      eventIndex: eventIndex,
      triggerIndex: triggerIndex,
      triggerName: triggerName,
      description: et.triggerDescriptions?.[triggerName] || '',
      media: et.triggerMedia?.[triggerName] || []
    });
    setIsDetailsModalOpen(true);
    setOpenPopovers({});
  };

  const handleDetailMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploadingDetailMedia(true);
    try {
      const uploadedMedia = [...(detailsModalConfig.media || [])];
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          alert(`File ${file.name} is too large (max 20MB)`);
          continue;
        }
        const formDataToSend = new FormData();
        formDataToSend.append("files", file);

        const response = await CommanServices.uploadGenericFile(formDataToSend);
        if (response.status) {
          uploadedMedia.push(response.data);
        } else {
          alert(`Failed to upload ${file.name}: ${response.message}`);
        }
      }
      setDetailsModalConfig(prev => ({
        ...prev,
        media: uploadedMedia
      }));
    } catch (error) {
      console.error("Error uploading detail media:", error);
      alert("Error uploading file");
    } finally {
      setIsUploadingDetailMedia(false);
    }
  };

  const handleRemoveDetailMedia = (index) => {
    setDetailsModalConfig(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const saveDetailsModal = () => {
    const { type, eventIndex, triggerName, description, media } = detailsModalConfig;

    const updatedEventTriggers = value.map((et, index) => {
      if (index === eventIndex) {
        if (type === 'event') {
          return {
            ...et,
            description: description,
            media: media
          };
        } else {
          const triggerDescriptions = { ...(et.triggerDescriptions || {}) };
          const triggerMedia = { ...(et.triggerMedia || {}) };

          if (description.trim() === '') {
            delete triggerDescriptions[triggerName];
          } else {
            triggerDescriptions[triggerName] = description;
          }

          if (media.length === 0) {
            delete triggerMedia[triggerName];
          } else {
            triggerMedia[triggerName] = media;
          }

          return {
            ...et,
            triggerDescriptions,
            triggerMedia
          };
        }
      }
      return et;
    });

    onChange(updatedEventTriggers);
    setIsDetailsModalOpen(false);
  };

  // Helper function to get severity styling
  const getSeverityStyle = (severity) => {
    return SEVERITY_LEVELS[severity] || { bgColor: 'bg-gray-100', textColor: 'text-gray-800', borderColor: 'border-gray-300' };
  };

  // Helper function to render severity dot
  const SeverityDot = ({ severity, size = 'w-3 h-3' }) => {
    const colors = {
      HIGH: 'bg-red-500',
      MEDIUM: 'bg-yellow-500', 
      LOW: 'bg-green-500'
    };
    return <div className={`${size} rounded-full ${colors[severity] || 'bg-gray-300'}`} />;
  };

  // Add a new event
  const addEvent = () => {
    if (!newEventName.trim()) return;
    
    // Check for duplicate event names (case-insensitive)
    const eventExists = value.some(
      eventTrigger => eventTrigger.event.toLowerCase() === newEventName.trim().toLowerCase()
    );
    
    if (eventExists) {
      alert('Event already exists. Please use a different name.');
      return;
    }

    const newEventTrigger = {
      event: newEventName.trim(),
      triggers: [],
      severity: null, // Event-level severity
      triggerSeverityOverrides: {} // Trigger-specific severity overrides
    };
    
    onChange([...value, newEventTrigger]);
    setNewEventName('');
  };

  // Remove an event and all its triggers
  const removeEvent = (eventIndex) => {
    const newEventTriggers = value.filter((_, index) => index !== eventIndex);
    onChange(newEventTriggers);
    
    // Clean up trigger input state
    const eventToRemove = value[eventIndex];
    if (eventToRemove) {
      const newInputs = { ...newTriggerInputs };
      delete newInputs[eventToRemove.event];
      setNewTriggerInputs(newInputs);
    }
  };

  // Set event severity
  const setEventSeverity = (eventIndex, severity) => {
    const updatedEventTriggers = value.map((et, index) => {
      if (index === eventIndex) {
        return {
          ...et,
          severity: severity
        };
      }
      return et;
    });
    onChange(updatedEventTriggers);
    setOpenPopovers({});
  };

  // Set trigger severity override
  const setTriggerSeverity = (eventIndex, triggerIndex, severity) => {
    const eventTrigger = value[eventIndex];
    const trigger = eventTrigger.triggers[triggerIndex];
    
    const updatedEventTriggers = value.map((et, index) => {
      if (index === eventIndex) {
        const newOverrides = { ...et.triggerSeverityOverrides };
        if (severity === null) {
          delete newOverrides[trigger]; // Remove override
        } else {
          newOverrides[trigger] = severity; // Set override
        }
        return {
          ...et,
          triggerSeverityOverrides: newOverrides
        };
      }
      return et;
    });
    onChange(updatedEventTriggers);
  };

  // Apply event severity to all triggers
  const applyEventSeverityToAllTriggers = (eventIndex) => {
    const eventTrigger = value[eventIndex];
    if (!eventTrigger.severity) return;

    const updatedEventTriggers = value.map((et, index) => {
      if (index === eventIndex) {
        return {
          ...et,
          triggerSeverityOverrides: {} // Clear all overrides
        };
      }
      return et;
    });
    onChange(updatedEventTriggers);
    setOpenPopovers({});
  };

  // Get effective severity for a trigger (3-tier inheritance)
  const getTriggerEffectiveSeverity = (eventTrigger, trigger) => {
    // 1. Trigger override (highest priority)
    
    if (eventTrigger.triggerSeverityOverrides?.[trigger]) {
      let severity = incidentSeverity
      switch (eventTrigger.triggerSeverityOverrides?.[trigger]?.toUpperCase()) {
        case "HIGH":
          severity = "HIGH"
          break;
        
        case "MEDIUM":
          severity = "MEDIUM"
          break;

        case "LOW":
          severity = "LOW"
          break;
      
        default:
          break;
      }
      
      // 2. Incident severity (inherited)
      return severity;
      }
    
    // 2. Event severity (middle priority)
    if (eventTrigger.severity) {
      return getEventEffectiveSeverity(eventTrigger);
    }
    
    // 3. Incident severity (lowest priority - inherited)
    return incidentSeverity;
  };


  const getEventEffectiveSeverity = (eventTrigger) => {
    let severity = incidentSeverity;

    const eventSeverity = eventTrigger?.severity?.toUpperCase();

    switch (eventSeverity) {
      case "HIGH":
        severity = "HIGH";
        break;

      case "MEDIUM":
        severity = "MEDIUM";
        break;

      case "LOW":
        severity = "LOW";
        break;

      default:
        // keep incidentSeverity
        break;
    }
    
    return severity;
  };

  // Add a trigger to a specific event
  const addTrigger = (eventIndex) => {
    const eventTrigger = value[eventIndex];
    const newTriggerValue = newTriggerInputs[eventTrigger.event] || '';
    
    if (!newTriggerValue.trim()) return;
    
    // Check for duplicate triggers within the same event (case-insensitive)
    const triggerExists = eventTrigger.triggers.some(
      trigger => trigger.toLowerCase() === newTriggerValue.trim().toLowerCase()
    );
    
    if (triggerExists) {
      alert('Trigger already exists for this event. Please use a different trigger.');
      return;
    }

    const updatedEventTriggers = value.map((et, index) => {
      if (index === eventIndex) {
        return {
          ...et,
          triggers: [...et.triggers, newTriggerValue.trim()]
        };
      }
      return et;
    });
    
    onChange(updatedEventTriggers);
    
    // Clear the input for this event
    setNewTriggerInputs({
      ...newTriggerInputs,
      [eventTrigger.event]: ''
    });
  };

  // Remove a trigger from a specific event
  const removeTrigger = (eventIndex, triggerIndex) => {
    const updatedEventTriggers = value.map((et, index) => {
      if (index === eventIndex) {
        return {
          ...et,
          triggers: et.triggers.filter((_, tIndex) => tIndex !== triggerIndex)
        };
      }
      return et;
    });
    
    onChange(updatedEventTriggers);
  };

  // Handle trigger input change
  const handleTriggerInputChange = (eventName, inputValue) => {
    setNewTriggerInputs({
      ...newTriggerInputs,
      [eventName]: inputValue
    });
  };

  // Handle Enter key for inputs
  const handleKeyPress = (e, action, ...args) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action(...args);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Severity Legend moved below Events section header */}

      {/* Add New Event */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Enter event name (e.g., Oxygen Pressure Alarm, Fire Alarm Activation, Network Down)"
            value={newEventName}
            onChange={(e) => setNewEventName(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, addEvent)}
            disabled={disabled}
            className="flex-1"
          />
          <Button 
            onClick={addEvent}
            disabled={!newEventName.trim() || disabled}
            size="sm"
            variant="secondary"
          >
            <Plus className="h-4 w-4" />
            Event
          </Button>
        </div>
      </div>

      {/* Display Events and their Triggers */}
      {value.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No events added yet</p>
          <p className="text-xs">Add an event above to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Tip text positioned immediately above first Event block */}
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            💡 Tip: Click an Event or a Trigger to set its alert severity.
          </div>
          {value.map((eventTrigger, eventIndex) => {
            const eventEffectiveSeverity = getEventEffectiveSeverity(eventTrigger);
            const eventSeverityStyle = getSeverityStyle(eventEffectiveSeverity);
            
            return (
              <div key={eventIndex} className="border rounded-lg p-4 bg-white">
                {/* Event processing starts here */}
                {/* Event Header with Severity */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Popover 
                      open={openPopovers[`event-${eventIndex}`] || false}
                      onOpenChange={(open) => setOpenPopovers(prev => ({...prev, [`event-${eventIndex}`]: open}))}
                    >
                      <PopoverTrigger asChild>
                        <Badge 
                          variant="outline" 
                          className={`cursor-pointer hover:shadow-md transition-all duration-200 ${eventSeverityStyle.bgColor} ${eventSeverityStyle.textColor} ${eventSeverityStyle.borderColor} flex items-center gap-2`}
                        >
                          <SeverityDot severity={eventEffectiveSeverity} size="w-2 h-2" />
                          Event: {eventTrigger.event}
                          {eventTrigger.description && <span className="text-[10px] select-none" title="Has description">📝</span>}
                          {eventTrigger.media && eventTrigger.media.length > 0 && <span className="text-[10px] select-none" title={`${eventTrigger.media.length} attachment(s)`}>📎</span>}
                          {!eventTrigger.severity && <span className="text-xs opacity-70">(inherited)</span>}
                        </Badge>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-3" align="start">
                        <div className="space-y-3">
                          <div className="text-sm font-medium">Set Event Severity</div>
                          <div className="space-y-2">
                            {Object.entries(SEVERITY_LEVELS).map(([key, level]) => (
                              <Button
                                key={key}
                                variant="ghost"
                                size="sm"
                                onClick={() => setEventSeverity(eventIndex, key)}
                                className={`w-full justify-start gap-2 ${
                                  eventTrigger.severity === key ? level.bgColor : 'hover:bg-gray-100'
                                }`}
                              >
                                <SeverityDot severity={key} size="w-3 h-3" />
                                {level.label}
                              </Button>
                            ))}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEventSeverity(eventIndex, null)}
                              className="w-full justify-start gap-2 text-gray-500 whitespace-normal"
                            >
                              Clear Event Severity (inherit incident)
                            </Button>
                          </div>
                          {eventTrigger.severity && eventTrigger.triggers.length > 0 && (
                            <div className="border-t pt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => applyEventSeverityToAllTriggers(eventIndex)}
                                className="w-full text-xs"
                              >
                                Apply Event Severity to All Triggers
                              </Button>
                            </div>
                          )}
                          <div className="border-t pt-3 mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEventDetailsModal(eventIndex)}
                              className="w-full text-xs font-semibold gap-1"
                            >
                              Edit Description & Media
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <span className="text-xs text-gray-500">
                      ({eventTrigger.triggers.length} triggers)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeEvent(eventIndex)}
                    disabled={disabled}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

              {/* Triggers for this Event */}
              <div className="ml-4 space-y-2">
                {/* Add Trigger Input */}
                <div className="flex gap-2 items-center">
                  <Input
                    placeholder="Add trigger (e.g., O₂ < 85 PSI, Low Level, Manual Pull Station, Heat Sensor Activated)"
                    value={newTriggerInputs[eventTrigger.event] || ''}
                    onChange={(e) => handleTriggerInputChange(eventTrigger.event, e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, addTrigger, eventIndex)}
                    disabled={disabled}
                    className="flex-1 text-sm"
                  />
                  <Button 
                    onClick={() => addTrigger(eventIndex)}
                    disabled={!newTriggerInputs[eventTrigger.event]?.trim() || disabled}
                    size="sm"
                    variant="secondary"
                  >
                    <Plus className="h-4 w-4" />
                    Trigger
                  </Button>
                </div>

                {/* Display Triggers with Severity */}
                {eventTrigger.triggers.length === 0 ? (
                  <p className="text-xs text-gray-400 italic ml-2">
                    No triggers added yet for this event
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2 ml-2">
                    {eventTrigger.triggers.map((trigger, triggerIndex) => {
                      const effectiveSeverity = getTriggerEffectiveSeverity(eventTrigger, trigger);
                      const isOverridden = eventTrigger.triggerSeverityOverrides?.[trigger] !== undefined;
                      const triggerSeverityStyle = effectiveSeverity ? getSeverityStyle(effectiveSeverity) : {};
                      
                      return (
                        <div key={triggerIndex} className="relative group">
                          <Popover 
                            open={openPopovers[`trigger-${eventIndex}-${triggerIndex}`] || false}
                            onOpenChange={(open) => setOpenPopovers(prev => ({...prev, [`trigger-${eventIndex}-${triggerIndex}`]: open}))}
                          >
                            <PopoverTrigger asChild>
                              <Badge
                                variant="secondary"
                                className={`cursor-pointer hover:shadow-md transition-all duration-200 flex items-center gap-1 ${
                                  effectiveSeverity 
                                    ? `${triggerSeverityStyle.bgColor} ${triggerSeverityStyle.textColor} ${triggerSeverityStyle.borderColor}` 
                                    : 'bg-gray-50 text-gray-700 border-gray-200'
                                }`}
                              >
                                {effectiveSeverity && <SeverityDot severity={effectiveSeverity} size="w-2 h-2" />}
                                {trigger}
                                {eventTrigger.triggerDescriptions?.[trigger] && <span className="text-[9px] select-none" title="Has description">📝</span>}
                                {eventTrigger.triggerMedia?.[trigger] && eventTrigger.triggerMedia[trigger].length > 0 && <span className="text-[9px] select-none" title={`${eventTrigger.triggerMedia[trigger].length} attachment(s)`}>📎</span>}
                                {isOverridden && <span className="text-xs opacity-70">(override)</span>}
                                {!disabled && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeTrigger(eventIndex, triggerIndex);
                                    }}
                                    className="ml-1 hover:bg-red-100 rounded-full p-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </Badge>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-3" align="start">
                              <div className="space-y-3">
                                <div className="text-sm font-medium">Set Trigger Severity</div>
                                <div className="space-y-2">
                                  {Object.entries(SEVERITY_LEVELS).map(([key, level]) => (
                                    <Button
                                      key={key}
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setTriggerSeverity(eventIndex, triggerIndex, key);
                                        setOpenPopovers({});
                                      }}
                                      className={`w-full justify-start gap-2 ${
                                        getTriggerEffectiveSeverity(eventTrigger, trigger) === key ? level.bgColor : 'hover:bg-gray-100'
                                      }`}
                                    >
                                      <SeverityDot severity={key} size="w-3 h-3" />
                                      {level.label}
                                    </Button>
                                  ))}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setTriggerSeverity(eventIndex, triggerIndex, null);
                                      setOpenPopovers({});
                                    }}
                                    className="w-full justify-start gap-2 text-gray-500"
                                  >
                                    Reset to Event ({eventTrigger.severity ? SEVERITY_LEVELS[eventTrigger.severity]?.label || 'None' : 'None'})
                                  </Button>
                                </div>
                                <div className="border-t pt-3 mt-3">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openTriggerDetailsModal(eventIndex, triggerIndex, trigger)}
                                    className="w-full text-xs font-semibold gap-1"
                                  >
                                    Edit Description & Media
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Summary */}
      {value.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700">
            📊 Summary: {value.length} event{value.length !== 1 ? 's' : ''} with{' '}
            {value.reduce((total, et) => total + et.triggers.length, 0)} total trigger{value.reduce((total, et) => total + et.triggers.length, 0) !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Details Editing Dialog */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              Edit Details for {detailsModalConfig.type === 'event' ? 'Event: "' + (value[detailsModalConfig.eventIndex]?.event || '') + '"' : 'Trigger: "' + detailsModalConfig.triggerName + '"'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                placeholder="Enter description here..."
                value={detailsModalConfig.description}
                onChange={(e) => setDetailsModalConfig(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Attachments</label>
              <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 flex flex-col items-center justify-center relative hover:bg-gray-100 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleDetailMediaUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploadingDetailMedia}
                />
                <div className="flex flex-col items-center gap-1 text-center pointer-events-none">
                  {isUploadingDetailMedia ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                      <span>Uploading file...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-blue-600 font-medium text-sm">Click to upload files</span>
                      <span className="text-[10px] text-gray-500">Max 20MB per file</span>
                    </>
                  )}
                </div>
              </div>

              {/* Detail Previews */}
              {detailsModalConfig.media && detailsModalConfig.media.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {detailsModalConfig.media.map((item, index) => {
                    const isImage = item.content_type?.startsWith("image/");
                    const isVideo = item.content_type?.startsWith("video/");
                    return (
                      <div key={index} className="relative w-16 h-16 rounded-md border overflow-hidden bg-gray-100">
                        {isImage ? (
                          <AuthenticatedMedia as="img" src={item.url} alt={item.filename} className="object-cover w-full h-full" />
                        ) : isVideo ? (
                          <AuthenticatedMedia as="video" src={item.url} className="object-cover w-full h-full" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-1">
                            <Paperclip className="w-4 h-4 text-gray-400 mb-1" />
                            <span className="text-[9px] text-gray-500 font-bold truncate max-w-full" title={item.filename}>{item.filename}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveDetailMedia(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-90 hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsDetailsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveDetailsModal} disabled={isUploadingDetailMedia}>
              Save Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventTriggerManager;
